import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Copy, Check, Download, ThumbsUp, ThumbsDown, Sparkles } from 'lucide-react';
import { BookOpen, GraduationCap, FileText } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';

interface ResultCardProps {
  title: string;
  authors: string;
  year: string;
  journal: string;
  snippet: string;
  url: string;
  citation: string;
  source: 'PubMed' | 'Semantic Scholar' | 'CrossRef';
  aiInsight?: string;
}

export const ResultCard = ({
  title,
  authors,
  year,
  journal,
  snippet,
  url,
  citation,
  source,
  aiInsight,
}: ResultCardProps) => {
  const [copied, setCopied] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<'positive' | 'negative' | null>(null);
  const { toast } = useToast();
  const { t } = useLanguage();


  const sourceColors = {
    'PubMed': 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
    'Semantic Scholar': 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20',
    'CrossRef': 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20'
  };

  const sourceIcons = {
    'PubMed': BookOpen,
    'Semantic Scholar': GraduationCap,
    'CrossRef': FileText
  };

  const SourceIcon = sourceIcons[source];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(citation);
      setCopied(true);
      toast({
        title: t.referenceCopied,
        description: t.referenceCopiedDescription,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: t.copyError,
        description: t.copyErrorDescription,
        variant: "destructive",
      });
    }
  };

  const generateBibTeX = () => {
    const cleanTitle = title.replace(/[{}]/g, '');
    const firstAuthor = authors.split(',')[0].trim().replace(/\s+/g, '');
    const key = `${firstAuthor}${year}`;
    
    return `@article{${key},
  author = {${authors}},
  title = {${cleanTitle}},
  journal = {${journal}},
  year = {${year}},
  url = {${url}}
}`;
  };

  const generateRIS = () => {
    return `TY  - JOUR
AU  - ${authors.split(',').join('\nAU  - ')}
TI  - ${title}
JO  - ${journal}
PY  - ${year}
UR  - ${url}
ER  -`;
  };

  const generateAPA = () => {
    return `${authors} (${year}). ${title}. ${journal}.`;
  };

  const handleExport = (format: 'bibtex' | 'ris' | 'apa' | 'vancouver') => {
    let content = '';
    let filename = '';
    let mimeType = 'text/plain';

    switch (format) {
      case 'bibtex':
        content = generateBibTeX();
        filename = `reference-${year}.bib`;
        mimeType = 'application/x-bibtex';
        break;
      case 'ris':
        content = generateRIS();
        filename = `reference-${year}.ris`;
        mimeType = 'application/x-research-info-systems';
        break;
      case 'apa':
        content = generateAPA();
        filename = `reference-${year}-apa.txt`;
        break;
      case 'vancouver':
        content = citation;
        filename = `reference-${year}-vancouver.txt`;
        break;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: t.referenceExported,
      description: `${t.referenceExportedDescription} ${format.toUpperCase()}`,
    });
  };

  const handleFeedback = async (type: 'positive' | 'negative') => {
    try {
      const { data, error } = await supabase.functions.invoke('submit-feedback', {
        body: {
          result_title: title,
          result_url: url,
          feedback_type: type,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setFeedbackGiven(type);
      toast({
        title: t.feedbackThanks,
        description: t.feedbackHelpsImprove,
      });
    } catch (error) {
      console.error('Feedback error:', error);
      toast({
        title: "Error",
        description: "No s'ha pogut registrar la teva opinió.",
        variant: "destructive",
      });
    }
  };


  return (
    <Card className="hover:shadow-xl transition-all duration-300 rounded-xl border-2 shadow-md">
      <CardHeader>
        <div className="space-y-3">
          <Badge variant="outline" className={`${sourceColors[source]} font-semibold w-fit`}>
            <SourceIcon className="h-3.5 w-3.5 mr-1.5" />
            {source}
          </Badge>
          <CardTitle className="text-lg leading-tight">{title}</CardTitle>
          <CardDescription className="text-sm">
            {authors} • {journal} • {year}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* AI Contextual Insight */}
        {aiInsight && (
          <div className="bg-gradient-to-br from-primary/5 to-accent/10 p-4 rounded-lg border-l-4 border-primary shadow-sm">
            <div className="flex items-start gap-2">
              <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground mb-1.5">{t.articleContext}</p>
                <p className="text-sm text-foreground/90 leading-relaxed">
                  {aiInsight}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {snippet && snippet !== 'Fragmento no disponible' && snippet !== 'Fragment not available' && snippet !== 'Fragment no disponible' && (
          <div className="bg-muted/50 p-4 rounded-md border border-border space-y-2">
            <p className="text-sm font-semibold text-foreground mb-2">{t.relevantSnippet}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {snippet}
            </p>
          </div>
        )}

        <div className="bg-accent/50 p-3 rounded-md">
          <p className="text-sm text-muted-foreground font-medium mb-1">{t.reference}</p>
          <p className="text-sm">{citation}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="flex-1 sm:flex-none"
          >
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                {t.copied}
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                {t.copyReference}
              </>
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <Download className="mr-2 h-4 w-4" />
                {t.export}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('bibtex')}>
                BibTeX (.bib)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('ris')}>
                RIS (.ris)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('apa')}>
                APA (texto plano)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('vancouver')}>
                Vancouver (texto plano)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="default"
            size="sm"
            asChild
            className="flex-1 sm:flex-none bg-primary hover:bg-primary/90"
          >
            <a href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              {t.viewArticle}
            </a>
          </Button>
        </div>

        {/* Feedback Section */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/50">
          <span className="text-xs text-muted-foreground mr-2">{t.articleHelpful}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleFeedback('positive')}
            disabled={feedbackGiven !== null}
            className={`h-8 w-8 p-0 hover:bg-green-500/10 ${
              feedbackGiven === 'positive' ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
            }`}
          >
            <ThumbsUp className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleFeedback('negative')}
            disabled={feedbackGiven !== null}
            className={`h-8 w-8 p-0 hover:bg-red-500/10 ${
              feedbackGiven === 'negative' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
            }`}
          >
            <ThumbsDown className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
